import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Trophy, Fire, Crown } from "@phosphor-icons/react";
import client from "../lib/api";

const ROLE_LABEL = { photographer: "Photographers", pilot: "Pilots", agent: "Agents", employee: "Employees" };
const MEDAL = ["#E9C77E", "#C7CBD1", "#CD8B5B"]; // gold / silver / bronze

const Avatar = ({ name, avatar }) =>
  avatar ? (
    <img src={avatar} alt="" className="h-8 w-8 rounded-full object-cover" />
  ) : (
    <div className="h-8 w-8 rounded-full bg-[#4A0000] text-white flex items-center justify-center text-xs font-semibold">
      {(name || "?").slice(0, 1).toUpperCase()}
    </div>
  );

function Board({ rows, metric }) {
  if (!rows || rows.length === 0)
    return <p className="text-sm text-[#9A968E] py-6 text-center" data-testid="leaderboard-empty">No entries yet — be the first.</p>;
  return (
    <ul className="space-y-1.5">
      {rows.map((r, i) => (
        <li key={r.user_id} data-testid={`leaderboard-row-${r.user_id}`}
          className={`flex items-center gap-3 rounded-[6px] px-3 py-2.5 border transition-colors ${r.is_me ? "border-[#4A0000]/30 bg-[#4A0000]/[0.05]" : "border-transparent hover:bg-[#F7F4EC]"}`}>
          <span className="w-6 text-center shrink-0">
            {i < 3 ? <Crown size={17} weight="fill" style={{ color: MEDAL[i] }} /> : <span className="text-sm font-semibold text-[#9A968E] tabular-nums">{i + 1}</span>}
          </span>
          <Avatar name={r.name} avatar={r.avatar} />
          <span className="text-sm font-medium text-[#2C2626] truncate flex-1">
            {r.name}{r.is_me && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-[#B08D57] font-semibold">You</span>}
          </span>
          {metric === "streak" ? (
            <span className="flex items-center gap-1 text-sm font-semibold text-[#4A0000] tabular-nums">
              <Fire size={15} weight="fill" className="text-[#B08D57]" />{r.streak}
              {r.best > r.streak && <span className="text-[10px] text-[#9A968E] font-normal">best {r.best}</span>}
            </span>
          ) : (
            <span className="text-right">
              <span className="text-sm font-semibold text-[#4A0000] tabular-nums">{r.points.toLocaleString()}</span>
              <span className="block text-[10px] text-[#9A968E] leading-none">{r.level}</span>
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

export default function Leaderboard() {
  const [d, setD] = useState(null);
  const [view, setView] = useState("streaks"); // primary: streaks | secondary: points
  const [role, setRole] = useState(null);

  useEffect(() => {
    client.get("/rewards/leaderboard").then((r) => {
      setD(r.data);
      const myRole = r.data?.me?.role;
      const streakRoles = Object.keys(r.data.streaks || {});
      setRole(streakRoles.includes(myRole) ? myRole : streakRoles[0]);
    }).catch(() => setD(null));
  }, []);

  if (!d) return null;

  // Visibility rules: agents never see the leaderboard; crew (photographer/pilot/dual)
  // only see photographer & pilot boards; admin/employee see everything.
  const myRole = d?.me?.role;
  if (myRole === "agent") return null;
  const isStaff = myRole === "admin" || myRole === "employee";
  const allowedRoles = isStaff ? null : ["photographer", "pilot"];

  const boards = view === "streaks" ? d.streaks : d.points;
  let roleKeys = Object.keys(boards).filter((k) => view === "points" || (boards[k] && k));
  if (allowedRoles) roleKeys = roleKeys.filter((k) => allowedRoles.includes(k));
  const active = role && boards[role] && roleKeys.includes(role) ? role : roleKeys[0];
  const leader = (boards[active] || [])[0];
  const leaderHasScore = leader && (view === "streaks" ? leader.streak > 0 : leader.points > 0);

  const switchView = (v) => {
    setView(v);
    const keys = Object.keys(v === "streaks" ? d.streaks : d.points);
    const myRole = d?.me?.role;
    setRole(keys.includes(myRole) ? myRole : keys[0]);
  };

  return (
    <motion.div variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0 } }}
      className="space-y-4" data-testid="rewards-leaderboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-xs font-semibold tracking-[0.15em] uppercase text-[#5C5A58] flex items-center gap-1.5">
          <Trophy size={16} weight="duotone" className="text-[#B08D57]" />Leaderboard
        </p>
        <div className="inline-flex rounded-full border border-[#E9DDC6] bg-white p-0.5">
          <button data-testid="leaderboard-view-streaks" onClick={() => switchView("streaks")}
            className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors ${view === "streaks" ? "bg-[#4A0000] text-white" : "text-[#5C5A58]"}`}>
            <Fire size={13} weight="fill" className="inline mr-1 -mt-0.5" />Delivery streaks
          </button>
          <button data-testid="leaderboard-view-points" onClick={() => switchView("points")}
            className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors ${view === "points" ? "bg-[#4A0000] text-white" : "text-[#5C5A58]"}`}>
            Points leaders
          </button>
        </div>
      </div>

      <div className="rounded-[8px] border border-[#E9DDC6] bg-white p-4 sm:p-5">
        <div className="flex flex-wrap gap-1.5 mb-4 border-b border-[#F0ECE2] pb-3">
          {roleKeys.map((k) => (
            <button key={k} data-testid={`leaderboard-role-${k}`} onClick={() => setRole(k)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${active === k ? "bg-[#B08D57]/15 text-[#4A0000]" : "text-[#8A8681] hover:bg-[#F7F4EC]"}`}>
              {ROLE_LABEL[k] || k}
            </button>
          ))}
        </div>

        {leaderHasScore && (
          <div data-testid="leaderboard-spotlight"
            className="relative overflow-hidden mb-4 rounded-[8px] border border-[#B08D57]/35 bg-gradient-to-r from-[#4A0000] to-[#5C1A1A] px-5 py-4 text-white">
            <Crown size={90} weight="fill" className="absolute -right-3 -top-4 text-white/10" />
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#E9C77E] flex items-center gap-1.5">
              {view === "streaks" ? <Fire size={13} weight="fill" /> : <Trophy size={13} weight="fill" />}
              This month's #1 · {ROLE_LABEL[active] || active}
            </p>
            <div className="flex items-center gap-3 mt-2">
              <Avatar name={leader.name} avatar={leader.avatar} />
              <div className="min-w-0">
                <p className="font-display text-xl leading-tight truncate">
                  {leader.name}{leader.is_me && <span className="ml-2 text-[11px] uppercase tracking-wide text-[#E9C77E] font-semibold">That's you!</span>}
                </p>
                <p className="text-sm text-white/75">
                  {view === "streaks"
                    ? <><Fire size={13} weight="fill" className="inline -mt-0.5 mr-1 text-[#E9C77E]" />{leader.streak}-shoot on-time streak{leader.best > leader.streak ? ` · best ${leader.best}` : ""}</>
                    : <>{leader.points.toLocaleString()} points · {leader.level}</>}
                </p>
              </div>
            </div>
          </div>
        )}

        <Board rows={boards[active]} metric={view === "streaks" ? "streak" : "points"} />
        {view === "streaks" && (
          <p className="text-[11px] text-[#9A968E] mt-4 text-center">Ranked by your current on-time delivery streak. Keep delivering on time to climb.</p>
        )}
      </div>
    </motion.div>
  );
}
